/**
 * Interface for ConfigFilterItem that represent a filter props.
 *
 * @interface ConfigFilterItem
 * @property {string} label - The label of a filter.
 * @property apiFilterFieldName - Field name for generating search params.
 * @property filterType - Type for filter
 * @property UrlParam - Mapping of url params and apiFilterFieldName (temporary a string to avoid, dependency from Portals)
 * @property emptyValue - The value will be deleted from request
 */

export const enum FilterTypes {
	VALUE = 'VALUE',
	ID = 'ID',
	MULTI_VALUE = 'MULTI_VALUE',
	BOOLEAN = 'BOOLEAN',
	DATE = 'DATE'
}

export interface ConfigFilterItem<apiFilterFieldName, defaultValue> {
	label: string
	apiFilterFieldName: apiFilterFieldName
	filterType: FilterTypes
	UrlParam: string
	defaultValue: defaultValue
	emptyValue?: string
	placeholder?: string
}

export type ConfigFilter<T, K = string> = {
	[key in keyof T]: ConfigFilterItem<K, K>
}

export type FilterSuffixData = {
	urlParam: string
	label: string
}

export interface SearchFilterParams {
	filterType: FilterTypes
	apiSearchFieldName: string
	data: Key | number [] | string []
	label?: Key
	UrlParam: string
	UrlParamLabel?: string
	suffixData?: FilterSuffixData
	isMainSearch?: boolean
}

export interface FilterParams {
	filterType: FilterTypes
	apiFilterFieldName: string
	data: string | number | boolean | undefined | null | string[] | number []
	UrlParam: string
	UrlParamLabel?: string
	emptyValue?: string
	isSecondLoad?: boolean
	isDisabled?: boolean
}

export interface SearchOptionData extends SearchFilterParams {
	value: string
}

export enum OptionsSortType {
	default = 'default',
	usaFirst = 'usaFirst',
	byId = 'byId'
}

/**
 * Interface for ConfigItem that represent an option props.
 *
 * @interface ConfigItem
 * @property {string} label - The label of a category for SearchInput.
 * @property {string} apiFieldName - Response of Search results for SearchInput options. The value null for not grouped options
 * @property apiSearchFieldName - Field name for generating search params.
 * @property filterType - Type for transforming data for apiSearchFieldName.
 * @property UrlParam - Mapping of url params and apiSearchFieldName (temporary string)
 * @property UrlParamLabel - Mapping of url params and label
 * @property {boolean} isLabelId - Display entity ID instead of entity name
 * @property {boolean} customOptionFormatter - Format search options to LabelValue
 * @property {SuffixData} suffixData - an option label suffix data that can be displayed by condition
 */

export type SuffixData = {
	urlParam: string
	label: string
	render: <FormatterEntity extends Entity>(entity: FormatterEntity) => React.ReactNode
	checkHasSuffix: <FormatterEntity extends Entity>(entity: FormatterEntity) => boolean | undefined
}
export interface ConfigItem<apiFieldName, FormatterEntity = {}> {
	label?: string
	apiFieldName: apiFieldName | null
	apiSearchFieldName: string
	filterType: FilterTypes
	UrlParam: string
	UrlParamLabel?: string
	suffixData?: SuffixData
	isLabelId?: boolean
	isLabelCode?: boolean
	isLabelState?: boolean
	isTurkishUser?: boolean
	isLabelAndId?: boolean
	isLabelInValue?: boolean
	isLocationIdName?: boolean
	optionsSortType?: OptionsSortType
	customOptionFormatter?: (entity: FormatterEntity) => LabelValue | LabelValue<number>
	isMainSearch?: boolean
}

export type SearchConfigType<Field, FormatterEntity = {}> = {
	[K: string]: ConfigItem<Field | null, FormatterEntity>
}

export type getSearchFilterByFieldNames = (
	fieldsNames: SearchFilterParams['apiSearchFieldName'][]
) => SearchFilterParams | null

export type FormatOptionsConfigType<T> = {
	[K in keyof T]: ConfigItem<T>
}

export type FormatOptionsType<T> = {
	[K in keyof T]: (Entity | Key)[]
}
